const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const isLocalDevelopment =
  typeof window !== 'undefined' && LOCAL_HOSTS.includes(window.location.hostname);
const API_URL = isLocalDevelopment ? 'http://localhost:8001' : (process.env.REACT_APP_BACKEND_URL || '');

const requestAdmin = async (path, token, options = {}) => {
  const response = await fetch(`${API_URL}/api/admin/labor-intake${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'تعذر تنفيذ طلب إدارة القضايا العمالية.');
  return data;
};

export const laborAdminStatuses = [
  { value: 'new', label: 'جديد', tone: 'bg-[#eef3f5] text-[#4c6374] border-[#dce5e8]' },
  { value: 'under_review', label: 'تحت المراجعة', tone: 'bg-[#f7f0e3] text-[#896735] border-[#eadbbd]' },
  { value: 'missing_required', label: 'نواقص مطلوبة', tone: 'bg-[#faf1e8] text-[#99623b] border-[#ecdacb]' },
  { value: 'waiting_client', label: 'بانتظار العميل', tone: 'bg-[#f3eff1] text-[#705c6a] border-[#e3dbe0]' },
  { value: 'waiting_documents', label: 'بانتظار استكمال النواقص', tone: 'bg-[#f7f0e3] text-[#896735] border-[#eadbbd]' },
  { value: 'documents_completed', label: 'تم استكمال النواقص', tone: 'bg-[#eaf3ef] text-[#486e5c] border-[#d4e4dc]' },
  { value: 'missing_review', label: 'بانتظار مراجعة النواقص المستكملة', tone: 'bg-[#eaf3ef] text-[#486e5c] border-[#d4e4dc]' },
  { value: 'complete', label: 'مكتمل', tone: 'bg-[#eaf3ef] text-[#486e5c] border-[#d4e4dc]' },
  { value: 'preliminarily_accepted', label: 'مقبول مبدئياً', tone: 'bg-[#edf4f0] text-[#46695c] border-[#d6e5dd]' },
  { value: 'incomplete', label: 'غير مكتمل', tone: 'bg-[#f8eeee] text-[#896264] border-[#e8d7d7]' },
  { value: 'rejected', label: 'مرفوض', tone: 'bg-[#f6eded] text-[#876060] border-[#e5d3d3]' },
  { value: 'assigned_lawyer', label: 'محال للمحامي', tone: 'bg-[#eef1f5] text-[#53647a] border-[#dce1e8]' },
  { value: 'archived', label: 'مؤرشف', tone: 'bg-[#f1f0ed] text-[#716e68] border-[#e2ded7]' },
];

export const laborAssignees = [
  { id: 'lawyer-rahman', name: 'أ. عبدالرحمن', role: 'محامٍ' },
  { id: 'lawyer-essa', name: 'أ. عيسى', role: 'محامٍ' },
  { id: 'intake-nawal', name: 'أ. نوال', role: 'موظف استقبال' },
  { id: 'intern-naif', name: 'أ. نايف', role: 'متدرب' },
];

const statusByValue = Object.fromEntries(laborAdminStatuses.map((status) => [status.value, status]));

const attachmentItems = (item) => [
  ...(item.attachments?.initial || []),
  ...(item.attachments?.additional || []),
];

const hasAttachment = (item, keys) =>
  attachmentItems(item).some((attachment) =>
    keys.some((key) =>
      `${attachment.typeKey || ''} ${attachment.typeLabel || ''} ${attachment.displayName || ''}`.includes(key)
    )
  );

const defaultMissingItems = (item) => {
  const missing = [];
  const add = (id, title, description) => missing.push({ id, title, description, status: 'required', automatic: true });

  if (!hasAttachment(item, ['identity', 'الهوية', 'الإقامة'])) {
    add('auto-identity', 'لم يتم رفع الهوية / الإقامة', 'إرفاق مستند إثبات الهوية للتحقق من بيانات مقدم الطلب.');
  }
  if (!hasAttachment(item, ['contract', 'employment_proof', 'العقد', 'علاقة العمل'])) {
    add('auto-contract', 'لم يتم رفع العقد أو إثبات العلاقة', 'مطلوب مستند يوضح علاقة العمل أو الاتفاق.');
  }
  if (item.compensation?.claims?.includes('أجور متأخرة') && !hasAttachment(item, ['bank_statement', 'كشف حساب'])) {
    add('auto-bank', 'لم يتم رفع كشف حساب بنكي', 'كشف الحساب مطلوب لمراجعة المطالبة المتعلقة بالأجور المتأخرة.');
  }
  if (
    (item.compensation?.gosiWageMatchesActual === 'لا' || item.compensation?.contractWageMatchesActual === 'لا') &&
    !hasAttachment(item, ['gosi', 'التأمينات', 'المدد والأجور'])
  ) {
    add('auto-gosi', 'لم يتم رفع شهادة المدد والأجور', 'المستند يساعد في مراجعة الاختلاف في بيانات الأجر.');
  }
  if (item.client?.currentEmploymentStatus === 'لا' && !item.contract?.endReason) {
    add('auto-end-reason', 'لم يتم توضيح سبب انتهاء العلاقة', 'يلزم تحديد سبب انتهاء علاقة العمل.');
  }
  if (item.contract?.noticeProvided === 'نعم' && !item.contract?.noticeDate) {
    add('auto-notice-date', 'لم يتم تحديد تاريخ الإشعار', 'يلزم إدخال تاريخ الإشعار المقدم للعميل.');
  }
  return missing;
};

const statusForCase = (item) => item.adminData?.status || item.status || 'new';

export const getLaborAdminRole = (user) => {
  const role = `${user?.role || ''}`.toLowerCase();
  const roleName = `${user?.roleName || ''}`;
  if (role.includes('lawyer') || roleName.includes('محام')) return 'lawyer';
  if (role.includes('intake') || role.includes('reception') || roleName.includes('استقبال')) return 'intake';
  if (role.includes('intern') || roleName.includes('متدرب')) return 'intern';
  if (role.includes('admin') || role.includes('manager') || roleName.includes('مدير')) return 'manager';
  return 'intake';
};

export const getLaborAdminAccess = (user) => {
  const role = getLaborAdminRole(user);
  return {
    role,
    isManager: role === 'manager',
    canSeeSensitive: role === 'manager' || role === 'lawyer',
    canEditStatus: role === 'manager' || role === 'lawyer',
    canAssign: role === 'manager',
    canArchive: role === 'manager',
    canDeleteAttachment: role === 'manager',
    canAddMissing: role === 'manager' || role === 'lawyer',
    canAddNotes: role === 'manager' || role === 'lawyer',
  };
};

const assigneeName = (id) => laborAssignees.find((assignee) => assignee.id === id)?.name || 'غير مسند';

export const getCaseCompletion = (item) => {
  const checks = [
    item.client?.fullName || item.client?.firstName,
    item.client?.nationalId,
    item.client?.mobile,
    item.company?.tradeName || item.company?.legalName,
    item.contract?.contractType,
    item.contract?.startDate,
    item.compensation?.totalSalary || item.compensation?.basicSalary,
    item.summary?.approvalStatus,
    item.declaration?.accepted,
    attachmentItems(item).length > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
};

export const normalizeLaborCase = (item) => {
  const automaticMissing = defaultMissingItems(item);
  const storedMissing = item.adminData?.missingItems || [];
  const manualMissing = storedMissing.filter((missingItem) => !missingItem.automatic);
  const reviewedAutomatic = Object.fromEntries(
    storedMissing.filter((missingItem) => missingItem.automatic).map((missingItem) => [missingItem.id, missingItem])
  );
  const missingItems = [
    ...automaticMissing.map((missingItem) => reviewedAutomatic[missingItem.id] || missingItem),
    ...manualMissing,
  ];
  const latestInternalAt = item.adminData?.activity?.[0]?.createdAt;

  return {
    ...item,
    adminData: {
      status: statusForCase(item),
      assigneeId: item.adminData?.assigneeId || '',
      missingItems,
      notes: item.adminData?.notes || [],
      activity: item.adminData?.activity || [],
      documentCompletionRequest: item.adminData?.documentCompletionRequest || null,
      archivedAt: item.adminData?.archivedAt || '',
      deletedAttachmentIds: item.adminData?.deletedAttachmentIds || [],
    },
    completion: getCaseCompletion(item),
    statusInfo: statusByValue[statusForCase(item)] || statusByValue.new,
    assigneeName: assigneeName(item.adminData?.assigneeId),
    lastUpdatedAt: latestInternalAt || item.submittedAt,
  };
};

const canViewCase = (item, user) => {
  const access = getLaborAdminAccess(user);
  if (access.role === 'manager' || access.role === 'intake') return true;
  const expectedAssignee = access.role === 'lawyer' ? `lawyer-${user?.id}` : `intern-${user?.id}`;
  const selectedAssignee = laborAssignees.find((assignee) => assignee.id === item.adminData?.assigneeId);
  const matchesName = selectedAssignee && `${user?.fullName || ''}`.includes(selectedAssignee.name.replace(/^أ\.\s*/, ''));
  return item.adminData?.assigneeId === expectedAssignee || item.adminData?.assigneeId === user?.id || matchesName;
};

export const getAdminLaborCases = async (user, token) => {
  const cases = await requestAdmin('/requests', token);
  return cases.map(normalizeLaborCase).filter((item) => canViewCase(item, user));
};

export const getAdminLaborCaseById = async (caseId, user, token) => {
  const item = normalizeLaborCase(await requestAdmin(`/requests/${caseId}`, token));
  return canViewCase(item, user) ? item : null;
};

export const updateLaborCaseStatus = async (caseId, status, user, token) =>
  getLaborAdminAccess(user).canEditStatus
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/status`, token, { method: 'PATCH', body: JSON.stringify({ status }) }))
    : null;

export const assignLaborCase = async (caseId, assigneeId, user, token) =>
  getLaborAdminAccess(user).canAssign
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/assignment`, token, { method: 'PATCH', body: JSON.stringify({ assignee_id: assigneeId }) }))
    : null;

export const archiveLaborCase = async (caseId, user, token) =>
  getLaborAdminAccess(user).canArchive
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/archive`, token, { method: 'POST' }))
    : null;

export const addLaborCaseMissingItem = async (caseId, payload, user, token) =>
  getLaborAdminAccess(user).canAddMissing
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/missing-items`, token, { method: 'POST', body: JSON.stringify(payload) }))
    : null;

export const updateLaborCaseMissingStatus = async (caseId, missingId, status, user, token) =>
  getLaborAdminAccess(user).canAddMissing
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/missing-items/${missingId}`, token, { method: 'PATCH', body: JSON.stringify({ status }) }))
    : null;

export const addLaborCaseNote = async (caseId, text, user, token) =>
  getLaborAdminAccess(user).canAddNotes
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/notes`, token, { method: 'POST', body: JSON.stringify({ text }) }))
    : null;

export const requestLaborDocumentCompletion = async (caseId, payload, user, token) =>
  getLaborAdminAccess(user).canAddMissing
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/document-completion-request`, token, { method: 'POST', body: JSON.stringify(payload) }))
    : null;

export const removeLaborAttachment = async (caseId, attachmentId, user, token) =>
  getLaborAdminAccess(user).canDeleteAttachment
    ? normalizeLaborCase(await requestAdmin(`/requests/${caseId}/attachments/${attachmentId}`, token, { method: 'DELETE' }))
    : null;

export const fetchLaborAttachmentFile = async ({ caseId, attachmentId, mode = 'download', token }) => {
  const response = await fetch(`${API_URL}/api/admin/labor-intake/requests/${caseId}/attachments/${attachmentId}/file?mode=${mode}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'تعذر فتح المرفق.');
  }
  const disposition = response.headers.get('content-disposition') || '';
  const utf8FilenameMatch = disposition.match(/filename\*=utf-8''([^;]+)/i);
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  return {
    blob: await response.blob(),
    filename: utf8FilenameMatch ? decodeURIComponent(utf8FilenameMatch[1]) : filenameMatch?.[1] || 'attachment',
  };
};

export const getVisibleAttachments = (item) =>
  attachmentItems(item).filter((attachment) => !item.adminData.deletedAttachmentIds.includes(attachment.id));
